<!-- Copyright (c) 2023, Alix Mougenot (Au pied des Arbres), alix.mougenot@gmail.com -->
<!-- See license file. -->
<script setup lang="ts">
import axios from "axios";
import { inject, onMounted } from "vue";
import { jwtDecode } from "jwt-decode";

import { errorHandlerKey, googleClientIDKey } from "@/logic/provide";
import { useLoginStore } from "@/stores/login";

const login_domain = import.meta.env.VITE_BACKEND_URL;
const login_url = `${login_domain}/login`;
const logout_url = `${login_domain}/logout`;
const renew_url = `${login_domain}/renew`;

const googleAuthClientId = inject(googleClientIDKey);
const localStorageKey = "cdo-paris-user";
const userStore = useLoginStore();
const onError = inject(errorHandlerKey);

var renewTimer = undefined as number | NodeJS.Timeout | undefined; // Timer variable for token renewal

// Resume session from local storage when a local token is available and valid
function resumeFromLocalStorage() {
  const jwt = localStorage.getItem(localStorageKey);
  if (jwt) {
    const decoded = jwtDecode(jwt) as any;
    if (!("exp" in decoded)) {
      onLogout({ invalidToken: true });
      return;
    }

    if (!("name" in decoded)) {
      onLogout({ invalidToken: true });
      return;
    }

    if (decoded.exp * 1000 < Date.now()) {
      onLogout({ invalidToken: true });
      return;
    }

    openSession(jwt);
  }
}

function openSession(jwt: string) {
  const decoded = jwtDecode(jwt) as any;

  if (!("name" in decoded)) {
    onError?.("Compte invalide, nom de l'utilsateur manquant");
    onLogout({ invalidToken: true });
    return;
  }

  axios
    .post(
      login_url,
      { googleIdToken: jwt },
      {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      },
    )
    .then((response) => {
      userStore.login({
        name: response.data.info.displayName || decoded.name,
        googleToken: jwt,
        token: response.data.token,
        id: response.data.info.id,
        legacyID: response.data.info.legacyID || undefined,
      });
      setupTokenRenew();
    })
    .catch((error) => {
      if (error?.response?.data?.error) {
        if (error.response.status === 401) {
          onError?.("Ce compte n'a pas access à l'application", error);
        } else {
          onError?.("La connexion a échouée: veuillez réessayer", error);
        }
      } else {
        onError?.("Erreur lors de la connexion", error);
      }
      onLogout({ invalidToken: true }); // At this point we cannot have a valid session token
    });
}

function setupAutoSignIn() {
  if (googleAuthClientId === undefined) {
    onError?.(
      "Erreur lors de la connexion: serveur Google non configuré",
      new Error("google project number is undefined"),
    );
    return;
  }

  const callback = (response: any) => {
    if (response.error) {
      onError?.("Connexion Google refusée", response.error);
      onLogout({ invalidToken: true });
      return;
    }
    if (!response || !response.credential) {
      onError?.(
        "Connexion Google refusée",
        new Error("no credentials in response"),
      );
      onLogout({ invalidToken: true });
      return;
    }
    const jwt = response.credential;
    if (jwt) {
      localStorage.setItem(localStorageKey, jwt);
    }
    openSession(jwt);
  };

  window.onSignIn = callback;
}

function tokenRenew() {
  if (!userStore.currentUser?.token) {
    // Not authenticated yet
    setupTokenRenew({ retry: true });
    return;
  }

  if (window.document.visibilityState !== "visible") {
    // Inactive window
    console.log("Application is not visible, not renewing token");
    setupTokenRenew({ retry: true });
    return;
  }

  // The bearer token is the renew token, client has to send the latest known token to be able to renew.
  // Only one client can access the App at a time, since multiple access will not renew tokens at the same pace.
  axios
    .get(renew_url, {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${userStore.currentUser.token}`,
      },
    })
    .then((response) => {
      userStore.updateToken(response.data.token);
    })
    .catch((error) => {
      var logoutOptions = { invalidToken: false };
      if (error?.response?.data?.error) {
        if (error.response.status === 401) {
          onError?.("Votre session a expirée", error);
          logoutOptions.invalidToken = true;
        } else {
          onError?.(
            "Erreur lors de l'authentification: veuillez recharger la page",
            error,
          );
        }
      } else {
        onError?.("Authentification impossible", error);
      }
      onLogout(logoutOptions);
    })
    .finally(() => {
      setupTokenRenew();
    });
}

function setupTokenRenew(opt?: { retry?: boolean }) {
  const retry = opt?.retry || false;
  const renewTime = retry ? 1000 * 60 : 1000 * 60 * 5; // 1 minute for retry or 5 minutes

  if (renewTimer) {
    clearTimeout(renewTimer);
  }

  renewTimer = setTimeout(() => tokenRenew(), renewTime);
}

const onLogout = (opt?: { invalidToken: boolean }) => {
  console.log("Logging out user");
  localStorage.removeItem(localStorageKey);
  const token = userStore.currentUser?.token;
  const tokenWasInvalidated = opt && "invalidToken" in opt && opt.invalidToken;
  if (token && !tokenWasInvalidated) {
    axios.get(logout_url, { headers: { Authorization: `Bearer ${token}` } });
  }
  userStore.logout();
};

const logoutCallback = () => onLogout();

resumeFromLocalStorage();

onMounted(() => {
  setupAutoSignIn();
});
</script>

<template>
  <div class="header-container">
    <div class="flex-row center">
      <div class="title-container">
        <img
          alt="CDO Paris"
          class="logo"
          src="@/assets/icons/logo_oncd.png"
          width="25"
          height="25"
        />
        <h3>CDO Paris</h3>
      </div>

      <div class="search flex-row">
        <div id="searchbar"></div>
      </div>
      <div id="toolbar" class="flex-row"></div>
    </div>

    <div class="login-container">
      <span
        v-if="userStore.currentUser?.token"
        @click.stop="logoutCallback"
        class="button"
      >
        Logout
      </span>
      <div v-else class="google_sign_in" ref="signIn">
        <div
          id="g_id_onload"
          :data-client_id="googleAuthClientId"
          data-context="signin"
          data-itp_support="true"
          data-callback="onSignIn"
        ></div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.header-container {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  flex-wrap: nowrap;
  margin: 0px var(--vp-margin-right);
  height: 100%;
}

#toolbar {
  min-width: 700px;
}

.google_sign_in {
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: 1em;
}

.search {
  min-width: 680px;
  border-radius: 6px;
  padding-top: 6px;
  padding-bottom: 6px;
  margin-right: 1em;

  #searchbar {
    flex-grow: 1;
  }
}

.login-container {
  display: flex;
  margin-right: 1em;
}

.title-container {
  display: flex;
  align-items: center;

  font-family: "kiona";
  letter-spacing: 0.06rem;
  width: calc(var(--vp-menu-width) - var(--vp-margin-right));
}

.flex-row {
  display: flex;
  flex-direction: row;
  align-items: baseline;
  column-gap: 1em;

  &.center {
    align-items: center;
  }
}
.logo {
  display: block;
  margin: 0 1em 0 0;
}
</style>
